/**
 * ShareEngagementDialog — SPEC-SHARE-03.
 *
 * Owner (or a can_invite member) shares an engagement with a registered
 * user by email, assigning a role (viewer/editor) and optional invite
 * permission. Lists current members; owner can change role / remove.
 *
 * Permission model (mirrors RLS, SPEC-SHARE-01):
 *   • Owner: full control — add (any role + can_invite), change role,
 *     remove.
 *   • can_invite member: may ADD only (viewer, can_invite=false); the UI
 *     hides role/can_invite controls and the member list management for
 *     non-owners.
 *   • Security is enforced DB-side; the UI just reflects it.
 */

import { Loader2, Trash2, UserPlus } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useHistoryStorage } from "@/services";
import type { EngagementMember, EngagementMemberRole } from "@/types";

interface ShareEngagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  engagementId: string;
  engagementName: string;
  /** True when the current user owns the engagement (full control). */
  isOwner: boolean;
}

export function ShareEngagementDialog({
  open,
  onOpenChange,
  engagementId,
  engagementName,
  isOwner,
}: ShareEngagementDialogProps) {
  const { t } = useTranslation();
  const storage = useHistoryStorage();

  const [members, setMembers] = React.useState<EngagementMember[] | null>(null);
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState<EngagementMemberRole>("viewer");
  const [canInvite, setCanInvite] = React.useState(false);
  const [adding, setAdding] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const list = await storage.listMembers(engagementId);
      setMembers(list);
    } catch (err) {
      console.error("[share] listMembers failed", err);
      setMembers([]);
    }
  }, [storage, engagementId]);

  React.useEffect(() => {
    if (open) {
      setMembers(null);
      void refresh();
    }
  }, [open, refresh]);

  async function handleAdd() {
    const trimmed = email.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      // Non-owners can only add viewers without invite rights (RLS also
      // enforces this); force-safe values defensively.
      const effRole: EngagementMemberRole = isOwner ? role : "viewer";
      const effCanInvite = isOwner ? canInvite : false;
      await storage.addMemberByEmail(
        engagementId,
        trimmed,
        effRole,
        effCanInvite,
      );
      toast.success(t("share.added", { email: trimmed }));
      setEmail("");
      setRole("viewer");
      setCanInvite(false);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("SHARE_USER_NOT_FOUND")) {
        toast.error(t("share.errorUserNotFound", { email: trimmed }));
      } else if (msg.includes("SHARE_RATE_LIMIT")) {
        toast.error(t("share.errorRateLimit"));
      } else if (msg.includes("SHARE_AUTH_REQUIRED")) {
        toast.error(t("share.errorAuthRequired"));
      } else {
        toast.error(t("share.errorGeneric"));
        console.error("[share] addMemberByEmail failed", err);
      }
    } finally {
      setAdding(false);
    }
  }

  async function handleRoleChange(
    member: EngagementMember,
    nextRole: EngagementMemberRole,
  ) {
    try {
      await storage.updateMember(
        engagementId,
        member.userId,
        nextRole,
        member.canInvite,
      );
      await refresh();
    } catch (err) {
      console.error("[share] updateMember failed", err);
      toast.error(t("share.errorGeneric"));
    }
  }

  async function handleInviteToggle(
    member: EngagementMember,
    nextCanInvite: boolean,
  ) {
    try {
      await storage.updateMember(
        engagementId,
        member.userId,
        member.role,
        nextCanInvite,
      );
      await refresh();
    } catch (err) {
      console.error("[share] updateMember (invite) failed", err);
      toast.error(t("share.errorGeneric"));
    }
  }

  async function handleRemove(member: EngagementMember) {
    try {
      await storage.removeMember(engagementId, member.userId);
      toast.success(t("share.removed", { email: member.email ?? member.userId }));
      await refresh();
    } catch (err) {
      console.error("[share] removeMember failed", err);
      toast.error(t("share.errorGeneric"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>{t("share.title")}</DialogTitle>
          <DialogDescription>
            {t("share.description", { name: engagementName })}
          </DialogDescription>
        </DialogHeader>

        {/* Add form */}
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="share-email">{t("share.emailLabel")}</Label>
            <Input
              id="share-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("share.emailPlaceholder")}
              data-testid="share-email-input"
            />
          </div>

          {isOwner && (
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label>{t("share.roleLabel")}</Label>
                <Select
                  value={role}
                  onValueChange={(v) => setRole(v as EngagementMemberRole)}
                >
                  <SelectTrigger className="w-[160px]" data-testid="share-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">
                      {t("share.role.viewer")}
                    </SelectItem>
                    <SelectItem value="editor">
                      {t("share.role.editor")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch
                  id="share-can-invite"
                  checked={canInvite}
                  onCheckedChange={setCanInvite}
                  data-testid="share-can-invite-switch"
                />
                <Label htmlFor="share-can-invite" className="cursor-pointer">
                  {t("share.canInviteLabel")}
                </Label>
              </div>
            </div>
          )}

          <Button
            onClick={() => void handleAdd()}
            disabled={adding || email.trim() === ""}
            className="gap-2"
            data-testid="share-add-button"
          >
            {adding ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <UserPlus className="h-4 w-4" aria-hidden="true" />
            )}
            {t("share.addButton")}
          </Button>
        </div>

        {/* Member list */}
        <div className="border-t pt-4 mt-2">
          <h3 className="text-sm font-semibold mb-3">
            {t("share.membersTitle")}
          </h3>
          {members === null ? (
            <p className="text-sm text-muted-foreground">
              {t("share.loading")}
            </p>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              {t("share.noMembers")}
            </p>
          ) : (
            <ul className="space-y-2" data-testid="share-member-list">
              {members.map((m) => (
                <li
                  key={m.userId}
                  className="flex flex-col gap-2 rounded-md border p-2 sm:flex-row sm:items-center sm:justify-between"
                  data-testid={`share-member-${m.userId}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {m.email ?? m.userId}
                    </p>
                    {m.fullName && (
                      <p className="text-xs text-muted-foreground truncate">
                        {m.fullName}
                      </p>
                    )}
                  </div>

                  {isOwner ? (
                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                      <Select
                        value={m.role}
                        onValueChange={(v) =>
                          void handleRoleChange(m, v as EngagementMemberRole)
                        }
                      >
                        <SelectTrigger className="w-[120px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">
                            {t("share.role.viewer")}
                          </SelectItem>
                          <SelectItem value="editor">
                            {t("share.role.editor")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1">
                        <Switch
                          checked={m.canInvite}
                          onCheckedChange={(v) => void handleInviteToggle(m, v)}
                          aria-label={t("share.canInviteLabel")}
                        />
                        <span className="text-xs text-muted-foreground">
                          {t("share.canInviteShort")}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleRemove(m)}
                        aria-label={t("share.remove")}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                      </Button>
                    </div>
                  ) : (
                    <Badge variant="secondary" className="shrink-0">
                      {m.role === "editor"
                        ? t("share.role.editor")
                        : t("share.role.viewer")}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
